import {
  createContext,
  useCallback,
  useContext,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';
import * as RDialog from '@radix-ui/react-dialog';
import type { GrantSyncState, CredentialStatus } from '../lib/types';
import { friendlyError, type FriendlyError } from '../lib/errors';
import { batteryInfo } from '../lib/battery';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';

export function Button({
  variant = 'secondary',
  size,
  className = '',
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' }) {
  return <button type={type} className={`btn btn-${variant} ${size === 'sm' ? 'btn-sm' : ''} ${className}`} {...rest} />;
}

export function PageHeader({ title, intro, actions }: { title: string; intro?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-head">
      <div>
        <h1>{title}</h1>
        {intro && <p>{intro}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function Panel({
  title,
  description,
  actions,
  children,
  className = '',
  headingLevel = 2,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  headingLevel?: 2 | 3;
}) {
  const H = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <section className={`panel ${className}`}>
      {(title || actions) && (
        <div className="panel-head">
          <div>
            {title && <H style={{ fontSize: headingLevel === 2 ? '1.35rem' : undefined }}>{title}</H>}
            {description && <p>{description}</p>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export type Tone = 'neutral' | 'green' | 'yellow' | 'red' | 'blue' | 'ink';
export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`badge ${tone === 'neutral' ? '' : `badge-${tone}`}`}>{children}</span>;
}

export type SyncState = 'synced' | 'pending' | 'revoke' | 'failed' | 'none';

/**
 * Two-stage badge that separates "Opgeslagen" (in Antreja) from "Op het slot" (confirmed by sync).
 */
export function SyncBadge({ state, count, age }: { state: SyncState; count?: number; age?: string }) {
  if (state === 'none') return <Badge>Geen slot</Badge>;
  const lockText =
    state === 'synced'
      ? 'Op het slot'
      : state === 'pending'
        ? `Wacht op sync${count ? ` (${count})` : ''}`
        : state === 'revoke'
          ? `Intrekking wacht${count ? ` (${count})` : ''}`
          : 'Sync mislukt';
  const cls = { synced: 'is-synced', pending: 'is-pending', revoke: 'is-revoke', failed: 'is-failed' }[state];
  return (
    <span className={`sync ${cls}`} title={age ? `Oudste openstaande wijziging: ${age}` : undefined}>
      <span className="stage-saved">Opgeslagen</span>
      <span className="stage-lock">
        <span className="dot" aria-hidden="true" />
        {lockText}
        {age && state !== 'synced' && <span className="sr-only">, oudste {age}</span>}
      </span>
    </span>
  );
}

export function grantSyncToState(s: GrantSyncState): SyncState {
  return s === 'synced' ? 'synced' : s === 'pending' ? 'pending' : s === 'failed' ? 'failed' : s === 'revoke_pending' ? 'revoke' : 'synced';
}

export const CREDENTIAL_STATUS: Record<CredentialStatus, { label: string; tone: Tone }> = {
  pending_enrollment: { label: 'Nog inleren', tone: 'yellow' },
  active: { label: 'Actief', tone: 'green' },
  revoke_pending: { label: 'Intrekking wacht op sync', tone: 'red' },
  revoked: { label: 'Ingetrokken', tone: 'neutral' },
  expired: { label: 'Verlopen', tone: 'neutral' },
};

/**
 * Batterijstand: icoon + percentage, rood ≤ 15 % ("Vervang batterijen"), oranje ≤ 30 %.
 * Met `measuredAt` ook "gemeten <tijd>": zonder gateway kan een meting dagen oud zijn.
 */
export function Battery({ pct, measuredAt, detail = false }: { pct: number | null; measuredAt?: string | null; detail?: boolean }) {
  const info = batteryInfo(pct, measuredAt ?? null);
  if (info.level === 'unknown') {
    return detail ? (
      <>
        <span className="muted">Onbekend</span>
        <span className="cell-sub">nog niet gemeten</span>
      </>
    ) : (
      <span className="muted">—</span>
    );
  }
  const warn = info.level === 'red' || info.level === 'orange';
  const title = `${info.label}${info.advice ? ` · ${info.advice}` : ''} · ${info.measured}`;
  return (
    <>
      <span className={`battery battery-${info.level}${info.level === 'red' ? ' low' : ''}`} title={title}>
        <span className="battery-bar" aria-hidden="true">
          <span style={{ width: `${Math.max(6, pct!)}%` }} />
        </span>
        {info.label}
        {warn && <span className="sr-only">{` ${info.advice}`}</span>}
      </span>
      {detail && (
        <span className="cell-sub">
          {info.level === 'red' && <strong className="battery-advice">{info.advice}</strong>}
          {info.level === 'red' && <br />}
          {info.measured}
          {info.stale && ' (kan nu lager zijn)'}
        </span>
      )}
    </>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  children: (props: { id: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean }) => ReactElement;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;
  const describedBy = [hint ? hintId : null, error ? errId : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children({ id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined })}
      {hint && (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="error" id={errId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

export function Loading({ label = 'Laden…' }: { label?: string }) {
  return (
    <p className="muted" role="status" style={{ padding: 20 }}>
      {label}
    </p>
  );
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Foutmelding: wat er gebeurde (titel), wat je nu doet (hint), optioneel opnieuw proberen,
 * en ingeklapt de ruwe technische details met kopieerknop voor support.
 */
export function ErrorNotice({
  error,
  onRetry,
  retryLabel = 'Opnieuw proberen',
  tone = 'danger',
}: {
  error: unknown;
  onRetry?: () => void;
  retryLabel?: string;
  tone?: 'danger' | 'warn';
}) {
  const f: FriendlyError = isFriendly(error) ? error : friendlyError(error);
  const [copied, setCopied] = useState<boolean | null>(null);
  return (
    <div className={`alert alert-${tone} error-notice`} role="alert">
      <p className="en-title">{f.title}</p>
      {f.hint && <p className="en-hint">{f.hint}</p>}
      {(onRetry || f.details) && (
        <div className="en-actions">
          {onRetry && (
            <Button size="sm" onClick={onRetry}>
              {retryLabel}
            </Button>
          )}
          <details className="en-tech">
            <summary>Technische details</summary>
            <pre>{f.details}</pre>
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                setCopied(await copyText(f.details));
                setTimeout(() => setCopied(null), 2500);
              }}
            >
              {copied === true ? 'Gekopieerd' : copied === false ? 'Kopiëren lukt niet; selecteer de tekst' : 'Kopieer details'}
            </Button>
          </details>
        </div>
      )}
    </div>
  );
}

function isFriendly(e: unknown): e is FriendlyError {
  return !!e && typeof e === 'object' && 'title' in e && 'hint' in e && 'details' in e && 'kind' in e;
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return <ErrorNotice error={friendlyError(error, 'Laden is niet gelukt.')} onRetry={onRetry ?? (() => window.location.reload())} retryLabel={onRetry ? 'Opnieuw proberen' : 'Pagina vernieuwen'} />;
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  wide,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="dialog-overlay" />
        <RDialog.Content className={`dialog ${wide ? 'wide' : ''}`}>
          <RDialog.Title asChild>
            <h2>{title}</h2>
          </RDialog.Title>
          {description ? (
            <RDialog.Description className="dialog-desc">{description}</RDialog.Description>
          ) : (
            <RDialog.Description className="sr-only">{title}</RDialog.Description>
          )}
          {children}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

// ---------- toasts ----------
interface Toast {
  id: number;
  text: string;
  kind: 'info' | 'error';
}
const ToastCtx = createContext<(text: string, kind?: Toast['kind']) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, kind: Toast['kind'] = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toast-region" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind === 'error' ? 'error' : ''}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);
